import {
  type LocaleIso,
  getCountryByLocale,
  getValidLocaleISO,
  PixumEnvUtils,
  SERVICE_TYPE,
  HTTP_METHODS,
  SERVICE_PAYLOAD,
  HTTP_CREDENTIALS_MODE,
  HTTP_CACHE_MODE,
  HTTP_CONTENT_TYPE,
  HTTP_STATUS_CODE,
  SERVICE_URL_MAP
} from 'utils';
import { ClientJwtManager } from '../jwt/ClientJwtManager';
import { serializePsiParams } from './serializePsiParams';
import type { ServicePayloadType, ServiceType, PsiRequestOptions, HttpRequestOptions, PsiResponse } from '../../types';
import { FetchError, InvalidParameterError, HttpError, PsiError, TokenRefreshError, NetworkError } from '../errors';

/**
 * Client-side fetch-based service transformer for AUTHENTICATED requests.
 *
 * ## Browser-Only Implementation
 *
 * This is the client-side version of FetchTransformer.
 * - Only imports ClientJwtManager (no server dependencies)
 * - Safe to bundle for browser
 * - Uses document.cookie for JWT storage
 * - Uses relative paths (/api/psi-service) to leverage Next.js proxy (avoids CORS)
 *
 * ## Design Pattern: Facade
 *
 * ClientFetch provides a simplified, unified interface for making authenticated
 * HTTP requests from browser/client components. It acts as a facade by hiding the complexity of:
 * - JWT token management (via ClientJwtManager)
 * - PSI service-specific formatting and parameter injection
 * - Error handling and transformation
 * - Locale-based parameter building
 *
 * Clients simply call makePsiRequest() or makeRequest() without needing to know
 * about JWT lifecycle, token injection, or PSI formatting details.
 *
 * Internally uses ClientJwtManager (Singleton Pattern) to prevent
 * parallel token refresh race conditions.
 *
 * Features:
 * - PSI service requests (makePsiRequest) - always authenticated
 * - Standard HTTP requests (makeRequest) - always authenticated
 * - Automatic JWT token management via ClientJwtManager (prevents parallel refresh)
 * - Locale-based parameter injection for PSI
 * - Additional headers support
 * - No caching - all fetch calls are made fresh
 *
 * ⚠️ CRITICAL - DO NOT USE FOR AUTH INITIALIZATION:
 * NEVER use this transformer for:
 * - refreshToken endpoints
 * - loginAsAnonymous endpoints
 *
 * Why? This transformer calls getValidJwtToken() which may trigger refresh/anonymous
 * login if token is missing/expired. Using it for those endpoints creates circular
 * dependency: refreshToken → getJwtToken → refreshToken → infinite loop.
 *
 * Solution: ClientJwtManager uses native fetch() for these endpoints.
 * Use ClientFetch for ALL OTHER endpoints (PSI, etc.).
 *
 * @example Client Component - PSI Request
 * ```typescript
 * 'use client';
 * const transformer = new ClientFetch();
 * const result = await transformer.makePsiRequest('support.ticket.createManual', {
 *   fromName: 'John Doe',
 *   fromEMail: 'john@example.com',
 *   subject: 'Help needed',
 *   text: 'I need assistance with...'
 * });
 * ```
 *
 * @example Client Component - PSI Request with Custom Headers
 * ```typescript
 * 'use client';
 * const transformer = new ClientFetch();
 * const result = await transformer.makePsiRequest(
 *   'cart.add.product',
 *   { productId: 123 },
 *   {
 *     headers: {
 *       'X-Custom-Header': 'value'
 *     }
 *   }
 * );
 * ```
 *
 * @example Client Component - Standard HTTP Request
 * ```typescript
 * 'use client';
 * const transformer = new ClientFetch();
 * const apiHost = process.env['NEXT_PUBLIC_API_HOST'];
 *
 * // Returns parsed JSON data automatically
 * const profile = await transformer.makeRequest(
 *   `${apiHost}/api/user/profile`,
 *   {
 *     method: 'GET',
 *     headers: {
 *       'X-Request-ID': '12345'
 *     }
 *   }
 * );
 * ```
 */
export class ClientFetch {
  readonly fetchType = 'ClientFetch';
  private readonly locale: LocaleIso;
  private readonly serviceType: ServiceType;

  /**
   * ⚠️ **IMPORTANT: Prefer ServerFetch or Server Actions instead**
   *
   * Use ServerFetch for server-side data fetching (Server Components).
   * Use Server Actions for form submissions and user interactions (even in Client Components).
   *
   * Only use ClientFetch when:
   * - Real-time updates (polling, live data that changes frequently)
   * - Client-side state synchronization with server
   * - Browser-only operations (third-party integrations, client-side APIs)
   *
   * @param serviceType - The service type to use (defaults to PSI)
   */
  constructor(serviceType: ServiceType = SERVICE_TYPE.PSI) {
    this.locale = this.detectLocale();
    this.serviceType = serviceType;
  }

  /**
   * Detects the current locale from browser environment.
   * Auto-detects from window.PIXUM_ENV.CONTENTFUL_LOCALE set during SSR initialization.
   * Falls back to 'de-DE' if not available.
   *
   * @returns Valid locale ISO string
   */
  private detectLocale(): LocaleIso {
    return getValidLocaleISO(PixumEnvUtils.get('CONTENTFUL_LOCALE') || 'de-DE');
  }

  /**
   * Gets a valid JWT token with automatic refresh handling.
   *
   * JWT Lifecycle (managed by ClientJwtManager singleton):
   * - SessionKeepAlive manages periodic refresh (main source)
   * - EDGE CASE: Handles 2-min interval where token may expire mid-request
   *
   * Singleton Pattern Benefits:
   * - Prevents parallel refresh: All ClientFetch instances share same refresh promise
   * - Memory efficient: Single JwtManager instance per browser tab
   * - Race condition safe: First request refreshes, others wait
   *
   * Decision tree (in ClientJwtManager.getValidJwtToken):
   * 1. Check if token exists in cookies → return immediately
   * 2. Check if refresh already in progress → wait for it
   * 3. If no refresh in progress → start new refresh and cache promise
   * 4. Return valid token
   *
   * @returns Valid JWT token (never null, never expired)
   */
  private async getValidJwtToken(): Promise<string> {
    try {
      return await ClientJwtManager.getValidJwtToken();
    } catch (error) {
      throw new TokenRefreshError(
        'Failed to get valid JWT token',
        {
          originalError: error instanceof Error ? error.message : String(error)
        },
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Serializes query params to a string, handling arrays (repeated keys) and filtering undefined.
   */
  private toQueryString(rawQuery: Record<string | number, unknown>): string {
    return Object.keys(rawQuery)
      .filter(key => rawQuery[key] !== undefined)
      .flatMap(key => {
        const value = rawQuery[key];

        if (Array.isArray(value)) {
          return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`);
        }

        return [`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`];
      })
      .join('&');
  }

  /**
   * Returns `?<queryString>` or empty string if no params provided.
   */
  private addQueryParams(rawQuery?: Record<string | number, unknown>): string {
    if (!rawQuery) return '';
    const queryString = this.toQueryString(rawQuery);

    return queryString ? `?${queryString}` : '';
  }

  /**
   * Returns the service URL for client-side requests.
   *
   * Client-side requests use relative paths to leverage Next.js proxy.
   * This avoids CORS issues by routing through Next.js API routes,
   * which then forward to the actual service.
   *
   * @returns Relative path to Next.js proxy (e.g., '/api/psi-service')
   * @throws Error if service type is not found in CLIENT map
   */
  private getServiceUrl(): string {
    const url = SERVICE_URL_MAP.CLIENT[this.serviceType];

    if (!url) {
      throw new InvalidParameterError(`No client URL configured for service type: ${this.serviceType}`, {
        serviceType: this.serviceType
      });
    }

    return url;
  }

  /**
   * Parses PSI endpoint format into service, module, and action.
   *
   * @param endpoint - PSI endpoint in format "service.module.action"
   * @returns Parsed endpoint parts
   * @throws Error if endpoint format is invalid
   */
  private parsePsiEndpoint(endpoint: string): { service: string; module: string; action: string } {
    const [service, module, action] = endpoint.split('.');

    if (!service || !module || !action) {
      throw new InvalidParameterError(`Invalid PSI endpoint format: ${endpoint}. Expected "service.module.action"`, {
        endpoint,
        expected: 'service.module.action'
      });
    }

    return { service, module, action };
  }

  /**
   * Creates brand-language-country parameter for PSI requests.
   * Format: {brandId},{languageId},{countryId}
   *
   * @returns Brand-language-country parameter (e.g., '1,0,1')
   * @example
   * 'de-DE' → '1,0,1' (brand:1, language:0=de, country:1=DE)
   * 'fr-BE' → '1,24,6' (brand:1, language:24=fr, country:6=BE)
   */
  private createBrandLanguageCountryParam(): string {
    const country = getCountryByLocale(this.locale);

    return `${country.brandId},${country.languageId},${country.countryId}`;
  }

  /**
   * Creates locale-based parameters for PSI requests.
   *
   * @returns Locale parameters (brandId, languageId, countryId, cl)
   */
  private createPsiLocaleParams(): Record<string, string> {
    const country = getCountryByLocale(this.locale);

    return {
      brandId: country.brandId.toString(),
      languageId: country.languageId.toString(),
      countryId: country.countryId.toString(),
      cl: this.createBrandLanguageCountryParam()
    };
  }

  /**
   * Creates PSI request URL and body based on HTTP method.
   *
   * @param options - Build options
   * @param options.service - PSI service name
   * @param options.module - PSI module name
   * @param options.action - PSI action name
   * @param options.params - Request parameters
   * @param options.method - HTTP method
   * @returns URL and optional body
   */
  private createPsiUrlAndBody(options: {
    service: string;
    module: string;
    action: string;
    params: Record<string, string | string[]>;
    method: string;
  }): { url: string; body?: string } {
    const { service, module, action, params, method } = options;

    const endpointQuery = new URLSearchParams({ service, module, action });
    const serviceUrl = this.getServiceUrl();
    let url = `${serviceUrl}/?${endpointQuery.toString()}`;
    let body: string | undefined;

    const serializedParams = serializePsiParams(params);

    if (method === HTTP_METHODS.GET) {
      url += `&${serializedParams.toString()}`;
    } else {
      body = serializedParams.toString();
    }

    return { url, body };
  }

  /**
   * Processes PSI response data extraction and error checking.
   *
   * @param response - Fetch response
   * @param dataType - Expected data type
   * @returns Parsed response data
   * @throws Error if response is not ok or PSI returns error code
   */
  private async processPsiResponse<T>(response: Response, dataType: ServicePayloadType): Promise<T> {
    if (!response.ok) {
      throw new HttpError(`PSI request failed: ${response.status} ${response.statusText}`, response.status, {
        url: response.url,
        statusText: response.statusText
      });
    }

    // Blob responses require type assertion (caller must specify T = Blob)
    if (dataType === SERVICE_PAYLOAD.BLOB) {
      return (await response.blob()) as T;
    }

    // JSON responses - PsiResponse<T> ensures response.data is typed as T
    const data: PsiResponse<T> = await response.json();

    // PSI response format: { response: { code: 0, data: {...}, message, messageLocalized } }
    if (data.response && data.response.code > 0) {
      throw new PsiError(
        data.response.messageLocalized || data.response.message || `PSI error code: ${data.response.code}`,
        data.response.code,
        {
          url: response.url,
          responseData: data.response.data
        }
      );
    }

    return data.response.data;
  }

  /**
   * Resolves the HTTP error message, preferring field-level localized messages.
   *
   * @param errorData - Parsed error response body
   * @param causesLocalized - First localized message found under causes, if any
   * @param response - Fetch response, used for the fallback message
   * @returns The most specific error message available
   */
  private getHttpErrorMessage(
    errorData: Record<string, unknown>,
    causesLocalized: string | undefined,
    response: Response
  ): string {
    if (causesLocalized !== undefined) {
      return causesLocalized;
    }

    if (typeof errorData['messageLocalized'] === 'string') {
      return errorData['messageLocalized'];
    }

    if (typeof errorData['message'] === 'string') {
      return errorData['message'];
    }

    if (typeof errorData['error'] === 'string') {
      return errorData['error'];
    }

    return `HTTP request failed: ${response.status} ${response.statusText}`;
  }

  /**
   * Processes HTTP response data extraction and error checking.
   *
   * @param response - Fetch response
   * @param dataType - Expected data type
   * @returns Parsed response data
   * @throws Error if response is not ok
   */
  private async processHttpResponse<T>(response: Response, dataType: ServicePayloadType): Promise<T> {
    // If response is an error, always try to parse as JSON to get error details
    if (!response.ok) {
      let errorData: Record<string, unknown>;

      try {
        errorData = await response.json();
      } catch {
        // If JSON parsing fails, error response had no parseable body
        errorData = {};
      }

      // Some services (e.g. auth signup) nest the localized error under causes.<field>.messageLocalized
      // rather than at the top level. Extract the first one found as a fallback.
      const causesLocalized = (() => {
        const causes = errorData['causes'];

        if (typeof causes !== 'object' || causes === null) return undefined;

        for (const entry of Object.values(causes as Record<string, unknown>)) {
          if (
            typeof entry === 'object' &&
            entry !== null &&
            typeof (entry as Record<string, unknown>)['messageLocalized'] === 'string'
          ) {
            return (entry as Record<string, unknown>)['messageLocalized'] as string;
          }
        }

        return undefined;
      })();

      const message = this.getHttpErrorMessage(errorData, causesLocalized, response);

      throw new HttpError(message, response.status, {
        url: response.url,
        statusText: response.statusText,
        responseData: errorData
      });
    }

    // Blob responses require type assertion (caller must specify T = Blob)
    if (dataType === SERVICE_PAYLOAD.BLOB) {
      return (await response.blob()) as T;
    }

    // JSON responses - TypeScript infers correct type from generic T
    return await response.json();
  }

  /**
   * Makes a PSI service request.
   *
   * Endpoint format: "service.module.action"
   * Example: "email.send.downloadThx"
   *
   * Automatically:
   * - Adds locale parameters (brandId, languageId, countryId, cl)
   * - Includes JWT token in Authorization header
   * - Refreshes token if expired
   *
   * @param endpoint - PSI endpoint in format "service.module.action"
   * @param parameters - Request parameters
   * @param options - Request options
   * @returns Promise resolving to response data
   */
  async makePsiRequest<T>(
    endpoint: string,
    parameters: Record<string, unknown> = {},
    options: PsiRequestOptions = {}
  ): Promise<T> {
    try {
      const { method = HTTP_METHODS.GET, headers = {}, dataType = SERVICE_PAYLOAD.JSON } = options;

      // 1. Parse endpoint into service, module, action
      const { service, module, action } = this.parsePsiEndpoint(endpoint);

      // 2. Create locale parameters and merge with user parameters
      const localeParams = this.createPsiLocaleParams();
      const allParams = Object.entries({ ...localeParams, ...parameters }).reduce(
        (acc, [key, value]) => {
          if (Array.isArray(value)) {
            acc[key] = value.map(String);
          } else {
            // Convert null/undefined to empty string for form-urlencoded
            acc[key] = value === null ? '' : String(value);
          }

          return acc;
        },
        {} as Record<string, string | string[]>
      );

      // 3. Get JWT token (always required for PSI)
      const jwtToken = await this.getValidJwtToken();

      // 4. Create URL and body based on HTTP method
      const { url, body } = this.createPsiUrlAndBody({
        service,
        module,
        action,
        params: allParams,
        method
      });

      // 5. Build request headers
      const requestHeaders: HeadersInit = {
        'Content-Type': HTTP_CONTENT_TYPE.FORM_URLENCODED,
        ...headers,
        Authorization: `Bearer ${jwtToken}`
      };

      // 6. Make request (no caching)
      let response: Response;

      try {
        response = await fetch(url, {
          method,
          headers: requestHeaders,
          body,
          cache: HTTP_CACHE_MODE.NO_STORE
        });
      } catch (error) {
        throw new NetworkError(
          'PSI request failed due to network error',
          {
            url,
            method,
            endpoint
          },
          error instanceof Error ? error : undefined
        );
      }

      // 7. Process response
      return await this.processPsiResponse<T>(response, dataType);
    } catch (error) {
      if (error instanceof FetchError) {
        throw error;
      }

      throw new NetworkError(
        'Unexpected error in PSI request',
        { endpoint, parameters },
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Makes a standard HTTP request with JWT authentication.
   *
   * For non-PSI services (REST APIs, microservices, etc.).
   * All requests include JWT token in Authorization header.
   *
   * Supports two URL formats:
   * - Relative path (starts with '/'): Automatically prepends service URL from SERVICE_URL_MAP
   *   Example: '/api/payment/process' → 'https://payment-service.pixum.com/api/payment/process'
   * - Full URL (starts with 'http://' or 'https://'): Used as-is for external APIs
   *   Example: 'https://external-api.com/endpoint'
   *
   * Features:
   * - Automatic UNAUTHORIZED (401) retry with token refresh
   * - JSON content-type by default
   * - Automatic response parsing (JSON or Blob)
   * - credentials: same-origin for cookie-based auth
   *
   * Note: For unauthenticated endpoints (refresh, loginAsAnonymous),
   * use native fetch() directly instead of this method.
   *
   * @param baseUrl - Full URL (for external APIs) or path (for microservices using SERVICE_URL_MAP)
   * @param options - Request options
   * @returns Promise resolving to parsed response data
   */
  async makeRequest<T>(baseUrl: string, options: HttpRequestOptions = {}): Promise<T> {
    const { method = HTTP_METHODS.GET, headers = {}, body, dataType = SERVICE_PAYLOAD.JSON, query, signal } = options;

    // Build full URL: if path starts with '/', prepend service URL from map
    const base = baseUrl.startsWith('/') ? `${this.getServiceUrl()}${baseUrl}` : baseUrl;
    const url = base + this.addQueryParams(query);

    // Always get JWT token - this transformer is for authenticated requests only
    const jwtToken = await this.getValidJwtToken();

    // Build request headers with JWT
    const requestHeaders: HeadersInit = {
      'Content-Type': HTTP_CONTENT_TYPE.JSON,
      ...headers,
      Authorization: `Bearer ${jwtToken}`
    };

    // Build fetch config
    const fetchConfig: RequestInit = {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
      credentials: HTTP_CREDENTIALS_MODE.SAME_ORIGIN, // Required for cookie-based auth in browser
      cache: HTTP_CACHE_MODE.NO_STORE,
      signal
    };

    // Make request (no caching)
    let response: Response;

    try {
      response = await fetch(url, fetchConfig);
    } catch (error) {
      throw new NetworkError(
        'HTTP request failed due to network error',
        {
          url,
          method
        },
        error instanceof Error ? error : undefined
      );
    }

    // Automatic UNAUTHORIZED retry with token refresh
    // Handles edge case where token expires mid-request (2-min interval)
    if (response.status === HTTP_STATUS_CODE.UNAUTHORIZED) {
      const newJwtToken = await this.getValidJwtToken();

      fetchConfig.headers = {
        ...fetchConfig.headers,
        Authorization: `Bearer ${newJwtToken}`
      };

      try {
        response = await fetch(url, fetchConfig);
      } catch (error) {
        throw new NetworkError(
          'HTTP request retry failed due to network error',
          {
            url,
            method,
            retry: true
          },
          error instanceof Error ? error : undefined
        );
      }
    }

    // Process response
    return await this.processHttpResponse<T>(response, dataType);
  }
}
